import { money } from "@core/shared/utils/money";
import { formatLocalizedDateTime } from "@core/shared/utils/locale-formatters";
import { URLS } from "@core/shared/utils/urls";
import { BaseLink } from "@design-system/components/link";
import { getCurrentLocaleCode, plainTrans, trans } from "@mongez/localization";
import { ReceiptText, X } from "lucide-react";
import { useOfflineDues } from "../hooks/use-offline-dues.hook";
import {
  monthDate,
  sessionsCountKey,
  totalOfflineDue,
} from "../utils/offline-dues";

function monthName(month: string) {
  return formatLocalizedDateTime(monthDate(month), {
    month: "long",
    year: "numeric",
  });
}

/**
 * Strip at the top of the header while the student owes a month fee: attended
 * offline sessions of a monthly group with no paid monthly invoice. "View
 * invoices" opens My invoices, where the pending month invoice is listed.
 */
export function OfflineDuesBanner() {
  const { months, isVisible, hide } = useOfflineDues();

  if (!isVisible) return null;

  const total = months.length > 1 ? totalOfflineDue(months) : null;
  const localeCode = getCurrentLocaleCode();

  return (
    <aside
      role="status"
      aria-label={plainTrans("sessionHistory.duesLabel")}
      className="w-full animate-fade-in bg-gradient-to-l from-rose-600 via-rose-500 to-orange-500 text-white shadow-[0_8px_24px_-16px_rgba(159,18,57,0.9)]"
    >
      <div className="container mx-auto flex items-center gap-2.5 px-3 py-2 sm:px-6">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-white/20 ring-1 ring-white/30">
          <ReceiptText size={18} strokeWidth={2.5} />
        </span>

        <div className="min-w-0 flex-1 leading-tight">
          <p className="text-sm font-black">
            {trans("sessionHistory.duesTitle")}
          </p>
          <ul className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] font-semibold">
            {months.map(due => (
              <li
                key={due.month}
                className="rounded-full bg-white/15 px-2 py-0.5 ring-1 ring-white/20"
              >
                <span className="font-black">
                  {plainTrans("sessionHistory.duesMonthFee", {
                    month: monthName(due.month),
                  })}
                </span>
                {due.amount !== null && (
                  <>
                    {" · "}
                    <span className="font-black">{money(due.amount)}</span>
                  </>
                )}
                {" · "}
                {plainTrans(sessionsCountKey(due.sessions, localeCode), {
                  count: due.sessions,
                })}
              </li>
            ))}
            {total !== null && (
              <li className="font-black">
                {plainTrans("sessionHistory.duesTotal", {
                  amount: money(total),
                })}
              </li>
            )}
          </ul>
        </div>

        <BaseLink
          to={URLS.sessions.invoices}
          className="shrink-0 whitespace-nowrap rounded-full bg-white px-3.5 py-1.5 text-xs font-black text-rose-600 shadow-md shadow-rose-950/20 transition hover:bg-rose-50 active:scale-95"
        >
          {trans("sessionHistory.duesDetails")}
        </BaseLink>

        <button
          type="button"
          onClick={hide}
          className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white/15 text-white/90 transition hover:bg-white/25 active:scale-95"
          aria-label={plainTrans("sessionHistory.duesHide")}
          title={plainTrans("sessionHistory.duesHide")}
        >
          <X size={15} strokeWidth={2.75} />
        </button>
      </div>
    </aside>
  );
}
